import * as React from 'react'
import {Drawer, Box, Typography, IconButton, Button, TextField, MenuItem, Alert} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import {sectionCardSx, primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, ERROR} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'

import {dims as fmtDims, kg, int} from '../format'
import {PACK_KIND_LABEL, packUsage, brandingLabel} from './state/packingModel'
import KeyValue from '../components/KeyValue'

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const alertSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75, '& .MuiAlert-message': {lineHeight: 1.55}}
const dangerBtnSx = {...primaryBtnSx, bgcolor: ERROR, '&:hover': {bgcolor: ERROR, filter: 'brightness(0.9)'}}

/**
 * Every OTHER material as a reassignment destination, with the option's own
 * label already carrying its volumetric figure (or its absence) so the
 * merchant never has to leave this drawer to know what they are picking.
 */
export function buildPackDestinationOptions(packs, excludeId) {
  return (packs || [])
    .filter((p) => p.id !== excludeId)
    .map((p) => ({
      id: p.id,
      label: p.label,
      volKg: p.volKg,
      optionLabel: `${p.label}  (${p.volKg == null ? 'no dimensions' : `${kg(p.volKg)} volumetric`})`,
    }))
}

/**
 * The "you must move these first" notice. Gated on TOTAL usage
 * (`skus + combos`), not on `skus` alone — this account's own seeded data
 * has a material (`BOX-GEN`) carried by 0 SKUs but 2 observed combos, and a
 * gate that only checked `skus` would tell the merchant "nothing moves"
 * while `REMOVE_PACK` (useFreezeStore.js) silently reassigns those 2 combos
 * out from under them. The sentence itself still names whichever of
 * SKUs/combos is actually nonzero, so it never claims a SKU is involved
 * when only combos are.
 */
export function usageNotice(usage) {
  const skus = usage.skus || 0
  const combos = usage.combos || 0
  const skuPart = skus > 0 ? `${int(skus)} SKU${skus === 1 ? '' : 's'}` : null
  const comboPart = combos > 0 ? `${int(combos)} observed combination${combos === 1 ? '' : 's'}` : null
  const subject = skuPart && comboPart
    ? `${skuPart} and ${comboPart} are`
    : skuPart
      ? `${skuPart} ${skus === 1 ? 'is' : 'are'}`
      : `${comboPart} ${combos === 1 ? 'is' : 'are'}`
  return `${subject} packed in this material. Volumetric weight is anchored to the material, so they cannot be left without one.`
}

/** The consequence line once a destination is actually chosen. */
export function removalConsequence(chosen) {
  if (!chosen) return ''
  return chosen.volKg != null
    ? `Their volumetric weight becomes ${kg(chosen.volKg)}, and the chargeable weight is recomputed as the higher of dead and volumetric.`
    : 'That material has no dimensions, so their volumetric weight stays as it is in the base data.'
}

/**
 * Footer label. Names SKUs when there are any (the common case, and the
 * brief's own literal wording); falls back to naming combos for the
 * `BOX-GEN`-shaped edge case above, rather than a nonsensical
 * "Remove and move 0 SKUs" while 2 combos actually move.
 */
export function moveCountLabel(usage) {
  const skus = usage.skus || 0
  const combos = usage.combos || 0
  if (skus > 0) return `Remove and move ${int(skus)} SKU${skus === 1 ? '' : 's'}`
  if (combos > 0) return `Remove and move ${int(combos)} combination${combos === 1 ? '' : 's'}`
  return 'Remove material'
}

/**
 * Removal drawer for one packing material — Packing Task 6. `state` is the
 * narrow slice threaded down from the one module-wide store; `onRemove`
 * calls straight into `actions.removePack(id, destId)`, which already
 * implements the reassign-and-recascade (see useFreezeStore.js's own
 * `REMOVE_PACK`) — this component only ever decides WHICH destination id
 * to pass, never touches `chargeKg`/`volKg` itself.
 *
 * Keyboard-dismissable / focus-return: MUI's Modal defaults, plus the
 * caller's `focusFallbackRef` fallback to the active rail item — the same
 * mechanism every other drawer in this module already uses.
 */
export default function PackRemoveDrawer({open, state, pack, onClose, onRemove}) {
  const usage = pack ? packUsage(state, pack.id) : {skus: 0, combos: 0}
  const total = usage.skus + usage.combos

  const destinationOptions = React.useMemo(
    () => (pack ? buildPackDestinationOptions(state.packs, pack.id) : []),
    [state.packs, pack],
  )

  const [destId, setDestId] = React.useState('')
  // Same adjusted-during-render reset every other drawer in this module
  // uses on the closed -> open transition, so a stale destination pick
  // never survives from the last material this one drawer instance removed.
  const [prevOpen, setPrevOpen] = React.useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) setDestId('')
  }

  const chosen = destinationOptions.find((d) => d.id === destId) || null
  const ready = total === 0 || !!chosen

  const handleSubmit = () => {
    if (!ready || !pack) return
    onRemove(pack.id, chosen ? chosen.id : null)
  }

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 560}}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
                Remove packing material
              </Typography>
              {pack && <Typography sx={labelSx}>{pack.label} · {pack.id}</Typography>}
            </Box>
            <IconButton onClick={onClose} aria-label='Close remove packing material drawer' size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        {pack && (
          <Box sx={{p: 2, overflowY: 'auto', flex: 1, display: 'grid', gap: GAP.md, alignContent: 'start'}}>
            <Box sx={sectionCardSx}>
              <KeyValue items={[
                {label: 'Branding', value: brandingLabel(pack.branding)},
                {label: 'Type', value: PACK_KIND_LABEL[pack.kind] || pack.kind},
                {label: 'Dimensions', value: fmtDims(pack.dims)},
                {label: 'Volumetric weight', value: pack.volKg == null ? '—' : kg(pack.volKg)},
                {label: 'SKUs using it', value: int(usage.skus)},
              ]} />
            </Box>

            {total === 0 ? (
              <Alert severity='info' sx={alertSx}>
                No SKU is packed in this material, so nothing moves.
              </Alert>
            ) : (
              <>
                <Alert severity='warning' sx={alertSx}>{usageNotice(usage)}</Alert>

                <TextField
                  select
                  size='small'
                  label='Move those SKUs to'
                  value={destId}
                  onChange={(e) => setDestId(e.target.value)}
                  sx={{fontFamily: FONT, fontSize: SIZE.xs}}
                >
                  {destinationOptions.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.optionLabel}</MenuItem>
                  ))}
                </TextField>

                {chosen && (
                  <Alert severity='info' sx={alertSx}>{removalConsequence(chosen)}</Alert>
                )}
              </>
            )}
          </Box>
        )}

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
          <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>
          <Button sx={dangerBtnSx} disabled={!ready} onClick={handleSubmit}>
            {total > 0 ? moveCountLabel(usage) : 'Remove material'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}
